import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { FIREBASE_DB } from '../firebase.config';
import { Clock, Pin, Calendar, AlertCircle, ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';
import { format, formatDistanceToNow, isFuture, isPast, isToday } from 'date-fns';
import { Helmet } from 'react-helmet';

const EmployeeAnnouncements = () => {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch all employee-specific bulletins
  useEffect(() => {
    const fetchBulletins = async () => {
      try {
        setLoading(true);
        const now = new Date();
        
        let bulletinsQuery = query(
          collection(FIREBASE_DB, 'bulletins'),
          where('isActive', '==', true),
          where('isPublic', '==', false), // Only employee-specific announcements
          orderBy('isPinned', 'desc'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(bulletinsQuery);
        const bulletinsList = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            expiresAt: doc.data().expiresAt?.toDate()
          }))
          .filter(bulletin => !bulletin.expiresAt || bulletin.expiresAt > now);
        
        setBulletins(bulletinsList);
      } catch (error) {
        console.error('Error fetching employee bulletins:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBulletins();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(bulletins.map(b => b.category))];

  // Filter and sort bulletins
  const filteredBulletins = bulletins
    .filter(bulletin => {
      // Category filter
      if (categoryFilter !== 'all' && bulletin.category !== categoryFilter) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          bulletin.title.toLowerCase().includes(query) ||
          bulletin.content.toLowerCase().includes(query)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort by pinned status first
      if (a.isPinned !== b.isPinned) return b.isPinned ? 1 : -1;
      
      // Then apply selected sort
      switch (sortBy) {
        case 'newest':
          return b.createdAt - a.createdAt;
        case 'oldest':
          return a.createdAt - b.createdAt;
        case 'expiring':
          if (!a.expiresAt && !b.expiresAt) return 0;
          if (!a.expiresAt) return 1;
          if (!b.expiresAt) return -1;
          return a.expiresAt - b.expiresAt;
        default:
          return 0;
      }
    });

  // Helper functions
  const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), 'MMM d, yyyy');
  };

  const getBulletinStatusBadge = (bulletin) => {
    if (bulletin.isPinned) {
      return {
        icon: <Pin size={14} className="mr-1" />,
        text: 'Pinned',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-800'
      };
    }
    
    if (bulletin.expiresAt) {
      if (isToday(new Date(bulletin.expiresAt))) {
        return {
          icon: <AlertCircle size={14} className="mr-1" />,
          text: 'Expires Today',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-800'
        };
      }
      if (isFuture(new Date(bulletin.expiresAt))) {
        const daysLeft = Math.ceil((new Date(bulletin.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 3) {
          return {
            icon: <Clock size={14} className="mr-1" />,
            text: `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`,
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800'
          };
        }
      }
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 rounded-lg p-4">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Employee Announcements | Scott Business Group</title>
        <meta name="description" content="Internal announcements and updates for Scott Business Group employees." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-100">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Employee Announcements</h1>
            
            {/* Search and Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search announcements..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Filter size={16} className="mr-2" />
                  Filters
                </button>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="expiring">Expiring Soon</option>
                </select>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="all">All Categories</option>
                      {categories
                        .filter(c => c !== 'all')
                        .map(category => (
                          <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Announcements List */}
          <div className="divide-y divide-gray-100">
            {filteredBulletins.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">No announcements match your filters.</p>
              </div>
            ) : (
              filteredBulletins.map(bulletin => {
                const statusBadge = getBulletinStatusBadge(bulletin);
                
                return (
                  <div 
                    key={bulletin.id} 
                    className={`p-6 transition-colors ${
                      expandedId === bulletin.id ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h2 
                            className="text-xl font-semibold text-gray-900 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => setExpandedId(expandedId === bulletin.id ? null : bulletin.id)}
                          >
                            {bulletin.title}
                          </h2>
                          {bulletin.isPinned && (
                            <Pin size={16} className="text-amber-500 ml-2 flex-shrink-0" />
                          )}
                        </div>
                        
                        <div className="mt-2">
                          <p className={`text-gray-600 leading-relaxed ${
                            expandedId === bulletin.id ? '' : 'line-clamp-2'
                          }`}>
                            {bulletin.content}
                          </p>
                          {bulletin.content.length > 120 && (
                            <button 
                              onClick={() => setExpandedId(expandedId === bulletin.id ? null : bulletin.id)} 
                              className="text-sm text-primary hover:text-primary-dark mt-2 flex items-center font-medium"
                            >
                              {expandedId === bulletin.id ? (
                                <>
                                  Show less <ChevronUp size={16} className="ml-1" />
                                </>
                              ) : (
                                <>
                                  Read more <ChevronDown size={16} className="ml-1" />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                            {bulletin.category || 'General'}
                          </span>
                          
                          {statusBadge && (
                            <span className={`inline-flex items-center px-3 py-1 rounded-full ${statusBadge.bgColor} ${statusBadge.textColor} text-sm font-medium`}>
                              {statusBadge.icon}
                              {statusBadge.text}
                            </span>
                          )}
                          
                          <span className="inline-flex items-center text-sm text-gray-500">
                            <Calendar size={14} className="mr-1" />
                            {formatDistanceToNow(new Date(bulletin.createdAt), { addSuffix: true })}
                          </span>
                          
                          {bulletin.expiresAt && (
                            <span className="inline-flex items-center text-sm text-gray-500">
                              <Clock size={14} className="mr-1" />
                              Expires: {formatDate(bulletin.expiresAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeAnnouncements; 